import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Collapse from "@material-ui/core/Collapse";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import TablePagination from "@material-ui/core/TablePagination";
import Button from "@material-ui/core/Button";
import AccountTreeIcon from "@material-ui/icons/AccountTree";

import { useSelector } from "react-redux";
import axios from "axios";
import envConfig from "../../bin/env.config";
import { buildProductTree } from "./productTreeBuild";
import {
  fetchAllProjectProductsFlat,
  fetchChildrenForParent,
  TREE_ROOT_KEY,
  parentToCacheKey,
} from "./projectProductsTreeApi";
import ButtonAdd from "./Products/ButtonAdd";
import ButtonEdit from "./Products/ButtonEdit";
import ButtonLlmSettings from "./Products/ButtonLlmSettings";
import ButtonDelete from "./Products/ButtonDelete";
import ButtonHidden from "./Products/ButtonHidden";
import ButtonProductNote from "./Products/ButtonProductNote";
import SearchBox from "./Products/SearchBox";
import BProductsPanel from "./Products/BProductsPanel";
import CompetitorsPanel from "./Products/CompetitorsPanel";
import ButtonMetrics from "./Projects/ButtonMetrics";
import ButtonConfig from "./Projects/ButtonConfig";
import ButtonProductNoteReport from "./Projects/ButtonProductNoteReport";
import ButtonNote from "./Projects/ButtonNote";
import DialogUpdateTreeOrder from "./Products/DialogUpdateTreeOrder";
import BPotentialPanel from "./Products/BPotentialPanel";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    flex: 1,
    minHeight: 0,
    overflow: "auto",
  },
  searchContainer: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
    display: "none",
    [theme.breakpoints.up("sm")]: {
      display: "block",
    },
  },
  splitRow: {
    flexWrap: "nowrap",
    alignItems: "stretch",
    minHeight: "calc(100vh - 180px)",
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      minHeight: "auto",
    },
  },
  treePane: {
    minWidth: 280,
    maxWidth: 420,
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "none",
      minHeight: 280,
    },
  },
  treePaper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    maxHeight: "calc(100vh - 180px)",
    [theme.breakpoints.down("sm")]: {
      maxHeight: 360,
    },
  },
  treePaperListScroll: {
    flex: 1,
    overflow: "auto",
    minHeight: 0,
  },
  treeOrdinal: {
    flexShrink: 0,
    marginRight: theme.spacing(0.5),
    fontFamily: "ui-monospace, monospace",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    minWidth: "1.5em",
    textAlign: "right",
  },
  detailPane: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
}));

function TreeBranch({
  node,
  depth,
  selectedId,
  onSelectProduct,
  expanded,
  onToggleExpand,
  ordinalPrefix,
  classes,
}) {
  const pid = node.product.id;
  const hasChildren = node.children.length > 0;
  const isOpen = expanded[pid] === true;
  const isSelected = selectedId === pid;

  return (
    <>
      <ListItem
        selected={isSelected}
        style={{ paddingLeft: Math.min(8 + depth * 14, 120) }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(pid);
            }}
            aria-label={isOpen ? "Collapse" : "Expand"}
          >
            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        ) : (
          <Box width={40} height={36} flexShrink={0} />
        )}
        <Box
          flex={1}
          minWidth={0}
          onClick={() => onSelectProduct(node.product)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelectProduct(node.product);
            }
          }}
          role="button"
          tabIndex={0}
          style={{ cursor: "pointer" }}
        >
          <ListItemText
            primary={
              <Box display="flex" alignItems="baseline" minWidth={0}>
                <Typography
                  component="span"
                  variant="caption"
                  className={classes.treeOrdinal}
                >
                  {ordinalPrefix}.
                </Typography>
                <Typography
                  component="span"
                  variant="body2"
                  noWrap
                  style={{ flex: 1, minWidth: 0 }}
                >
                  {node.product.name || "(no name)"}
                </Typography>
              </Box>
            }
            secondary={`#${node.product.id}`}
          />
        </Box>
      </ListItem>
      {hasChildren && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children.map((child, idx) => (
              <TreeBranch
                key={child.product.id}
                node={child}
                depth={depth + 1}
                selectedId={selectedId}
                onSelectProduct={onSelectProduct}
                expanded={expanded}
                onToggleExpand={onToggleExpand}
                ordinalPrefix={`${ordinalPrefix}.${idx + 1}`}
                classes={classes}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

function LazyTreeBranch({
  product,
  depth,
  selectedId,
  onSelectProduct,
  expanded,
  onToggleExpand,
  childProductsByParentKey,
  emptyByParent,
  loadingByParent,
  ordinalPrefix,
  classes,
}) {
  const pid = product.id;
  const pKey = String(pid);
  const isEmpty = emptyByParent[pKey] === true;
  const isOpen = expanded[pid] === true;
  const isSelected = selectedId === pid;
  const isLoading = loadingByParent[pKey] === true;
  const listKey = parentToCacheKey(pid);
  const childProducts = childProductsByParentKey[listKey] || [];
  const showChevron = !isEmpty;

  return (
    <>
      <ListItem
        selected={isSelected}
        style={{ paddingLeft: Math.min(8 + depth * 14, 120) }}
      >
        {showChevron ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(pid);
            }}
            disabled={isLoading}
            aria-label={isOpen ? "Collapse" : "Expand"}
          >
            {isLoading ? (
              <CircularProgress size={20} color="primary" />
            ) : isOpen ? (
              <ExpandLessIcon />
            ) : (
              <ExpandMoreIcon />
            )}
          </IconButton>
        ) : (
          <Box width={40} height={36} flexShrink={0} display="flex" alignItems="center" justifyContent="center" />
        )}
        <Box
          flex={1}
          minWidth={0}
          onClick={() => onSelectProduct(product)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelectProduct(product);
            }
          }}
          role="button"
          tabIndex={0}
          style={{ cursor: "pointer" }}
        >
          <ListItemText
            primary={
              <Box display="flex" alignItems="baseline" minWidth={0}>
                <Typography
                  component="span"
                  variant="caption"
                  className={classes.treeOrdinal}
                >
                  {ordinalPrefix}.
                </Typography>
                <Typography
                  component="span"
                  variant="body2"
                  noWrap
                  style={{ flex: 1, minWidth: 0 }}
                >
                  {product.name || "(no name)"}
                </Typography>
              </Box>
            }
            secondary={`#${product.id}`}
          />
        </Box>
      </ListItem>
      {isOpen && childProducts.length > 0 && (
        <Collapse in timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {childProducts.map((ch, idx) => (
              <LazyTreeBranch
                key={ch.id}
                product={ch}
                depth={depth + 1}
                selectedId={selectedId}
                onSelectProduct={onSelectProduct}
                expanded={expanded}
                onToggleExpand={onToggleExpand}
                childProductsByParentKey={childProductsByParentKey}
                emptyByParent={emptyByParent}
                loadingByParent={loadingByParent}
                ordinalPrefix={`${ordinalPrefix}.${idx + 1}`}
                classes={classes}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

export default function CompanyReportToolProductPageTree(props) {
  const history = useHistory();
  const classes = useStyles();
  const projectId = props.match.params.id;
  const token = useSelector((state) => state.session.token);

  const [isLoading, setIsLoading] = useState(false);
  const [project, setProject] = useState(null);
  const [flatProducts, setFlatProducts] = useState([]);
  const [reloadData, setReloadData] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [searchProjectProductOnly, setSearchProjectProductOnly] = useState(true);
  const [searchIsHidden, setSearchIsHidden] = useState(2);
  const [notMappedYet, setNotMappedYet] = useState(false);
  const [showPotential, setShowPotential] = useState(false);
  const [orderBy] = useState("name");
  const [order] = useState("asc");

  const [selectedProductId, setSelectedProductId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [reloadDataCount, setReloadDataCount] = useState(0);
  const [treePage, setTreePage] = useState(0);
  const [treeRowsPerPage, setTreeRowsPerPage] = useState(20);
  const [updateTreeOpen, setUpdateTreeOpen] = useState(false);
  const isFullListMode = Boolean(String(searchText || "").trim());

  const [childProductsByParentKey, setChildProductsByParentKey] = useState({});
  const [emptyByParent, setEmptyByParent] = useState({});
  const [loadingByParent, setLoadingByParent] = useState({});

  const loadAbortRef = useRef(null);

  const doReloadData = useCallback(() => {
    setReloadData((prev) => prev + 1);
  }, []);

  const handleUpdateTreeClose = useCallback(() => {
    setUpdateTreeOpen(false);
    doReloadData();
  }, [doReloadData]);

  const doReloadCount = useCallback(() => {
    setReloadDataCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const fetchProject = async () => {
      setIsLoading(true);
      try {
        const url = `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/project/${projectId}`;
        const result = await axios.get(url, {
          headers: {
            Authorization: token,
          },
        });
        setProject(result.data.data);
      } finally {
        setIsLoading(false);
      }
    };
    if (projectId && token) {
      fetchProject();
    }
  }, [projectId, token]);

  useEffect(() => {
    if (!projectId || !token) return;
    if (loadAbortRef.current) {
      try {
        loadAbortRef.current.abort();
      } catch (e) {
        /* ignore */
      }
    }
    const ac = new AbortController();
    loadAbortRef.current = ac;

    const run = async () => {
      if (isFullListMode) {
        setIsLoading(true);
        setChildProductsByParentKey({});
        setEmptyByParent({});
        setLoadingByParent({});
        setExpanded({});
        try {
          const products = await fetchAllProjectProductsFlat({
            projectId,
            token,
            orderBy,
            order,
            searchText,
            notMappedYet,
            searchIsHidden,
            signal: ac.signal,
          });
          if (!ac.signal.aborted) {
            setFlatProducts(products);
          }
        } catch (e) {
          if (e.name === "AbortError" || e.code === "ERR_CANCELED") return;
          console.error(e);
          if (!ac.signal.aborted) {
            setFlatProducts([]);
          }
        } finally {
          if (!ac.signal.aborted) {
            setIsLoading(false);
          }
        }
        return;
      }

      setFlatProducts([]);
      setIsLoading(true);
      setChildProductsByParentKey({});
      setEmptyByParent({});
      setLoadingByParent({ [TREE_ROOT_KEY]: true });
      setExpanded({});
      try {
        const list = await fetchChildrenForParent({
          projectId,
          token,
          parentId: null,
          orderBy,
          order,
          searchText: "",
          notMappedYet,
          searchIsHidden,
          signal: ac.signal,
        });
        if (ac.signal.aborted) {
          return;
        }
        setChildProductsByParentKey({ [TREE_ROOT_KEY]: list });
      } catch (e) {
        if (e.name === "AbortError" || e.code === "ERR_CANCELED") return;
        console.error(e);
        if (!ac.signal.aborted) {
          setChildProductsByParentKey({ [TREE_ROOT_KEY]: [] });
        }
      } finally {
        if (!ac.signal.aborted) {
          setLoadingByParent((s) => {
            const n = { ...s };
            delete n[TREE_ROOT_KEY];
            return n;
          });
          setIsLoading(false);
        }
      }
    };

    run();
    return () => {
      ac.abort();
    };
  }, [
    projectId,
    token,
    reloadData,
    isFullListMode,
    searchText,
    notMappedYet,
    searchIsHidden,
    orderBy,
    order,
  ]);

  const { roots: fullRoots, productsById: fullProductsById } = useMemo(
    () => buildProductTree(flatProducts),
    [flatProducts]
  );

  const productsById = useMemo(() => {
    if (isFullListMode) {
      return fullProductsById;
    }
    const acc = {};
    Object.keys(childProductsByParentKey).forEach((k) => {
      (childProductsByParentKey[k] || []).forEach((p) => {
        acc[p.id] = p;
      });
    });
    return acc;
  }, [isFullListMode, fullProductsById, childProductsByParentKey]);

  const rootProductList = useMemo(
    () =>
      isFullListMode
        ? null
        : childProductsByParentKey[TREE_ROOT_KEY] || [],
    [isFullListMode, childProductsByParentKey]
  );

  const pagedTreeSlice = useMemo(
    () => {
      if (isFullListMode) {
        return fullRoots.slice(
          treePage * treeRowsPerPage,
          treePage * treeRowsPerPage + treeRowsPerPage
        );
      }
      return (rootProductList || []).slice(
        treePage * treeRowsPerPage,
        treePage * treeRowsPerPage + treeRowsPerPage
      );
    },
    [isFullListMode, fullRoots, rootProductList, treePage, treeRowsPerPage]
  );

  const rootCount = isFullListMode
    ? fullRoots.length
    : (rootProductList || []).length;

  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(rootCount / treeRowsPerPage));
    const maxPage = Math.max(0, pageCount - 1);
    if (treePage > maxPage) {
      setTreePage(maxPage);
    }
  }, [rootCount, treeRowsPerPage, treePage]);

  const selectedProduct = selectedProductId
    ? productsById[selectedProductId]
    : null;

  useEffect(() => {
    if (selectedProductId && !productsById[selectedProductId]) {
      setSelectedProductId(null);
    }
  }, [selectedProductId, productsById]);

  const expandAncestors = useCallback(
    (product) => {
      const pr = product.parent_id;
      if (pr == null || pr === "") {
        return;
      }
      setExpanded((prev) => {
        const next = { ...prev };
        let idn = Number(pr);
        while (idn && !Number.isNaN(idn)) {
          next[idn] = true;
          const p2 = productsById[idn];
          if (!p2) {
            break;
          }
          const ppn = p2.parent_id;
          if (ppn == null || ppn === "") {
            break;
          }
          idn = Number(ppn);
        }
        return next;
      });
    },
    [productsById]
  );

  const handleSelectProduct = useCallback(
    (product) => {
      setSelectedProductId(product.id);
      expandAncestors(product);
    },
    [expandAncestors]
  );

  const handleToggleExpandFull = useCallback((id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleToggleExpandLazy = useCallback(
    async (id) => {
      if (expanded[id]) {
        setExpanded((p) => ({ ...p, [id]: false }));
        return;
      }
      const pKey = String(id);
      if (emptyByParent[pKey]) {
        return;
      }
      const ckey = parentToCacheKey(id);
      const list = childProductsByParentKey[ckey];
      if (list !== undefined) {
        if (list.length > 0) {
          setExpanded((prev) => ({ ...prev, [id]: true }));
        }
        return;
      }
      setLoadingByParent((s) => ({ ...s, [pKey]: true }));
      try {
        const list = await fetchChildrenForParent({
          projectId,
          token,
          parentId: id,
          orderBy,
          order,
          searchText: "",
          notMappedYet,
          searchIsHidden,
        });
        setChildProductsByParentKey((c) => ({
          ...c,
          [parentToCacheKey(id)]: list,
        }));
        if (list.length === 0) {
          setEmptyByParent((e) => ({ ...e, [pKey]: true }));
        } else {
          setExpanded((prev) => ({ ...prev, [id]: true }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingByParent((s) => {
          const n = { ...s };
          delete n[pKey];
          return n;
        });
      }
    },
    [
      expanded,
      projectId,
      token,
      orderBy,
      order,
      notMappedYet,
      searchIsHidden,
      emptyByParent,
      childProductsByParentKey,
    ]
  );

  const handleSearchIsHiddenChange = (event) => {
    setSearchIsHidden(event.target.value);
    setSelectedProductId(null);
  };

  const doSearch = (searchValue) => {
    setSearchText(searchValue);
    setSelectedProductId(null);
  };

  const searchTextForPanels = searchProjectProductOnly ? "" : searchText;

  return (
    <>
      <AppBar position="sticky" color="inherit">
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => history.goBack()}
            aria-label="Close"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className={classes.title} variant="h6" noWrap>
            Products (tree) — {project && project.name}
          </Typography>
          {project && <ButtonProductNoteReport projectId={project.id} />}
          {project && <ButtonNote projectId={project.id} />}
          {project && <ButtonConfig project={project} />}
          {project && <ButtonMetrics project={project} />}
        </Toolbar>
      </AppBar>

      <Grid
        className={classes.searchContainer}
        container
        direction="column"
        spacing={1}
      >
        <Grid item container alignItems="center">
          <Select
            value={searchIsHidden}
            onChange={handleSearchIsHiddenChange}
          >
            <MenuItem value="0">
              <em>All Products</em>
            </MenuItem>
            <MenuItem value="1">
              <em>Hidden Products</em>
            </MenuItem>
            <MenuItem value="2">
              <em>Unhidden Products</em>
            </MenuItem>
          </Select>
          <FormControlLabel
            control={
              <Checkbox
                checked={notMappedYet}
                onChange={() => setNotMappedYet(!notMappedYet)}
                name="Not mapped yet"
                color="secondary"
              />
            }
            label="Not mapped yet"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showPotential}
                onChange={() => setShowPotential(!showPotential)}
                name="Show Potential"
                color="secondary"
              />
            }
            label="Show Potential"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchProjectProductOnly}
                onChange={() =>
                  setSearchProjectProductOnly(!searchProjectProductOnly)
                }
                name="Product Only"
                color="secondary"
              />
            }
            label="Product Only"
          />
          <SearchBox
            value={searchText}
            doSearch={doSearch}
            searchOnChange={false}
            placeholder="Search"
          />
        </Grid>
      </Grid>

      <Grid
        container
        className={classes.splitRow}
        spacing={2}
        style={{ padding: 16, paddingTop: 0 }}
      >
        <Grid item className={classes.treePane}>
          <Box display="flex" alignItems="center" mb={1} flexWrap="wrap">
            <ButtonEdit project_id={projectId} reloadData={doReloadData} />
            <Button
              variant="contained"
              startIcon={<AccountTreeIcon />}
              style={{ marginLeft: 8 }}
              onClick={() => setUpdateTreeOpen(true)}
            >
              Update tree
            </Button>
            <DialogUpdateTreeOrder
              open={updateTreeOpen}
              onClose={handleUpdateTreeClose}
              flatListOverride={isFullListMode ? flatProducts : null}
              projectId={projectId}
              orderBy={orderBy}
              order={order}
              notMappedYet={notMappedYet}
              searchIsHidden={searchIsHidden}
            />
          </Box>
          <Paper className={classes.treePaper} variant="outlined">
            {isLoading && rootCount === 0 ? (
              <Box p={2} display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            ) : rootCount === 0 ? (
              <Box p={2}>
                <Typography variant="body2" color="textSecondary">
                  No products match the current filters.
                </Typography>
              </Box>
            ) : (
              <>
                <Box className={classes.treePaperListScroll}>
                  <List dense component="nav">
                    {isFullListMode
                      ? pagedTreeSlice.map((node, idx) => (
                          <TreeBranch
                            key={node.product.id}
                            node={node}
                            depth={0}
                            selectedId={selectedProductId}
                            onSelectProduct={handleSelectProduct}
                            expanded={expanded}
                            onToggleExpand={handleToggleExpandFull}
                            ordinalPrefix={String(
                              treePage * treeRowsPerPage + idx + 1
                            )}
                            classes={classes}
                          />
                        ))
                      : pagedTreeSlice.map((product, idx) => (
                          <LazyTreeBranch
                            key={product.id}
                            product={product}
                            depth={0}
                            selectedId={selectedProductId}
                            onSelectProduct={handleSelectProduct}
                            expanded={expanded}
                            onToggleExpand={handleToggleExpandLazy}
                            childProductsByParentKey={childProductsByParentKey}
                            emptyByParent={emptyByParent}
                            loadingByParent={loadingByParent}
                            ordinalPrefix={String(
                              treePage * treeRowsPerPage + idx + 1
                            )}
                            classes={classes}
                          />
                        ))}
                  </List>
                </Box>
                <TablePagination
                  component="div"
                  count={rootCount}
                  page={treePage}
                  onPageChange={(e, newPage) => setTreePage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setTreeRowsPerPage(parseInt(e.target.value, 10));
                    setTreePage(0);
                  }}
                  rowsPerPage={treeRowsPerPage}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              </>
            )}
          </Paper>
        </Grid>

        <Grid item className={classes.detailPane}>
          <Paper className={classes.root} variant="outlined">
            {!selectedProduct ? (
              <Typography color="textSecondary" variant="body1">
                Select a product in the tree to view details.
              </Typography>
            ) : (
              <>
                <Box mb={2} display="flex" flexWrap="wrap" alignItems="center">
                  <ButtonEdit product={selectedProduct} reloadData={doReloadData} />
                  <ButtonLlmSettings product={selectedProduct} project_id={projectId} reloadData={doReloadData} />
                  <ButtonDelete product={selectedProduct} reloadData={doReloadData} />
                  <ButtonHidden product={selectedProduct} reloadData={doReloadData} />
                  <ButtonAdd product={selectedProduct} reloadData={doReloadCount} />
                </Box>
                <Typography variant="subtitle1" gutterBottom>
                  {selectedProduct.name}{" "}
                  <ButtonProductNote productId={selectedProduct.id} />
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  <strong>ID:</strong> {selectedProduct.id} ·{" "}
                  <strong>Category:</strong> {selectedProduct.category || "—"}
                </Typography>
                <Typography variant="body2" paragraph style={{ fontSize: "0.85rem" }}>
                  <strong>Key words:</strong> {selectedProduct.key_words || "—"}
                </Typography>
                {(selectedProduct.number_pmg_need > 1 || selectedProduct.llm_meta) && (
                  <Typography variant="body2" paragraph style={{ fontSize: "0.85rem" }}>
                    <strong>PMG:</strong> {(selectedProduct.products?.length ?? 0)}/{selectedProduct.number_pmg_need ?? 1}
                    {selectedProduct.llm_meta?.product_name && (
                      <> · <strong>LLM:</strong> {selectedProduct.llm_meta.product_name}
                      {selectedProduct.llm_meta.size ? ` ${selectedProduct.llm_meta.size}` : ""}</>
                    )}
                  </Typography>
                )}

                {showPotential && (
                  <Box mb={2} style={{ backgroundColor: "#f5f5a44d" }}>
                    <BPotentialPanel
                      item={selectedProduct}
                      reloadDataCount={reloadDataCount}
                      setReloadDataCount={setReloadDataCount}
                      searchText={searchTextForPanels}
                      projectId={projectId}
                    />
                  </Box>
                )}
                <Box mb={2}>
                  <BProductsPanel
                    item={selectedProduct}
                    reloadDataCount={reloadDataCount}
                    searchText={searchTextForPanels}
                    projectId={projectId}
                  />
                </Box>
                <CompetitorsPanel
                  cproductId={selectedProduct.id}
                  reloadData={doReloadData}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
